<template>
  <a-layout fixed left-0 top-0 h-screen w-screen p-0 class="layout">
    <global-header show-site-switcher>
      <template #factory>
        <global-header-factory-switch
          v-if="currentFactory && showFactorySwitch"
          v-model:current-factory="currentFactory"
        />
      </template>
    </global-header>
    <a-layout>
      <a-layout-sider
        v-model:collapsed="collapsed"
        collapsible
        :width="200"
        style="background: #fff; border-right: 1px solid #e5e6eb; overflow: hidden"
      >
        <div class="sider-inner">
          <!-- Module selector dropdown：展开时显示完整模块选择器，折叠时只显示图标按钮 -->
          <div class="sidebar-header" :class="{ 'sidebar-header-collapsed': collapsed }">
            <a-dropdown :trigger="['click']">
              <div v-if="collapsed" class="module-selector-collapsed">
                <icon
                  v-if="appStore.currentSite?.meta?.icon"
                  :icon="appStore.currentSite.meta.icon"
                  :width="20"
                  :class="appStore.currentSite?.meta?.iconCls"
                  style="border-radius: 4px; padding: 1px"
                />
              </div>
              <div v-else class="module-selector">
                <icon
                  v-if="appStore.currentSite?.meta?.icon"
                  :icon="appStore.currentSite.meta.icon"
                  :width="18"
                  :class="appStore.currentSite?.meta?.iconCls"
                  style="flex-shrink: 0; border-radius: 3px; padding: 1px"
                />
                <span class="module-selector-text">{{ currentModuleTitle || '选择模块' }}</span>
                <span class="module-selector-arrow">▾</span>
              </div>
              <template #overlay>
                <a-menu>
                  <a-menu-item key="workplace" @click="router.push({ name: 'workplace' })">
                    <span style="display: flex; align-items: center; gap: 10px; font-size: 14px; padding: 4px 0">
                      <icon icon="mingcute:home-3-line" :width="18" />
                      首页
                    </span>
                  </a-menu-item>
                  <a-menu-divider />
                  <a-menu-item v-for="site in appStore.siteList" :key="site.name" @click="appStore.gotoSite(site.name)">
                    <span style="display: flex; align-items: center; gap: 10px; font-size: 14px; padding: 4px 0">
                      <icon
                        v-if="site.meta.icon"
                        :icon="site.meta.icon"
                        :width="18"
                        :class="site.meta.iconCls"
                        style="border-radius: 3px; padding: 1px"
                      />
                      {{ t(site.meta.title) }}
                    </span>
                  </a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>
          </div>
          <!-- Extra sidebar content slot (e.g. project selector) -->
          <slot name="sidebar-extra" :collapsed="collapsed"></slot>
          <!-- Sub-page menu：flex-1，独占滚动条 -->
          <div class="sider-menu-wrap">
            <a-menu
              v-model:selectedKeys="selectedPageKey"
              v-model:open-keys="openKeys"
              :items="props.overrideMenus || menus"
              mode="inline"
              :style="{ borderRight: 0 }"
            />
          </div>
        </div>
      </a-layout-sider>

      <a-layout>
        <a-layout-content style="background: #f5f6f8; overflow: auto">
          <slot name="content">
            <router-view v-slot="{ Component }">
              <keep-alive :max="10">
                <content-fade-view v-if="route.meta.keepAlive" :key="route.name" :page="Component" />
              </keep-alive>
              <content-fade-view v-if="!route.meta.keepAlive" :key="route.name" :page="Component" />
            </router-view>
          </slot>
        </a-layout-content>
      </a-layout>
    </a-layout>
  </a-layout>
</template>
<script setup lang="ts">
import ContentFadeView from '@/components/layout/content-fade-view/index.vue';
import type { Factory } from '@/components/widgets/factory-switch/config';
import { useAppStore, useResidentStore } from '@/store';
import type { PermissionPageKey as PermissionPageKeyType } from '@/types/permission/page';
import { menuUtil } from '@/utils';
import storage from '@/utils/storage';
import { Icon } from '@iconify/vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import GlobalHeaderFactorySwitch from '../global-header/global-factory-switch.vue';
import GlobalHeader from '../global-header/index.vue';

const props = defineProps<{
  overrideMenus?: any[];
  onNavigate?: (key: string) => void;
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const appStore = useAppStore();
const resident = useResidentStore();
const openKeys = ref<string[]>([]);
const COLLAPSED_STORAGE_KEY = 'sider-menu-collapsed';

function initCollapsed(): boolean {
  // 路由强制折叠（如个人看板、生产大屏）优先
  if (route.meta.collapseMenuSiderOnMounted) {
    return true;
  }
  // 否则读取用户上次的折叠偏好
  return storage.get(COLLAPSED_STORAGE_KEY) === 'true';
}

const collapsed = ref<boolean>(initCollapsed());

watch(collapsed, (val) => {
  storage.set(COLLAPSED_STORAGE_KEY, String(val));
});

const showFactorySwitch = computed(() => {
  // 生产质量管理和项目空间需要显示工厂切换
  return route.matched.some((e) => {
    const name = e.name as string;
    return name === 'production' || name === 'project' || name?.startsWith('project-');
  });
});

const currentFactory = computed({
  get() {
    return resident.currentFactory;
  },
  set(factory: Factory) {
    resident.currentFactory = factory;
  },
});

const selectedPageKey = computed({
  get: () => {
    return [route.name as PermissionPageKeyType];
  },
  set(key: PermissionPageKeyType[]) {
    if (props.onNavigate) {
      props.onNavigate(key[0]);
    } else {
      router.push({ name: key[0] });
    }
  },
});

const menus = computed(() => {
  return appStore.currentSiteMenus;
});

const currentModuleTitle = computed(() => {
  if (appStore.currentSite?.meta?.title) {
    return t(appStore.currentSite.meta.title);
  }
  return '';
});

const activeMenus = computed(() => props.overrideMenus || menus.value);

function collapseCurrentMenu() {
  const res = menuUtil.getMenuFullPath(activeMenus.value, route.name as string);
  const keys = res.slice(0, -1).map((e) => e.key);
  openKeys.value = keys;
}

// Re-expand menu when route changes (e.g. project switch)
watch(
  () => route.fullPath,
  () => {
    if (!collapsed.value) {
      collapseCurrentMenu();
    }
  },
);

onMounted(() => {
  if (!collapsed.value) {
    collapseCurrentMenu();
  }
});
</script>
<style scoped>
:deep(.ant-layout-sider-trigger) {
  background-color: #fff;
  color: #c9cdd4;
  border-top: 1px solid #f2f3f5;
  border-right: 1px solid #e5e6eb;
  height: 32px !important;
  line-height: 32px !important;
  font-size: 12px;
}

.sider-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sider-menu-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.sidebar-header {
  padding: 12px 12px 8px;
  border-bottom: 1px solid #f2f3f5;
  margin-bottom: 4px;
}

.sidebar-header-collapsed {
  padding: 12px 0 8px;
  display: flex;
  justify-content: center;
}

.module-selector-collapsed {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f7f8fa;
  cursor: pointer;
  transition: background 0.15s;
}

.module-selector-collapsed:hover {
  background: #e8f3ff;
}

.module-selector {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 8px;
  cursor: pointer;
  background: #f7f8fa;
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  transition: background 0.15s;
  border: none;
}

.module-selector:hover {
  background: #e8f3ff;
}

.module-selector-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.module-selector-arrow {
  color: #86909c;
  font-size: 11px;
}

/* Sub-page menu styling */
:deep(.ant-menu-inline) {
  padding: 4px 6px;
}

:deep(.ant-menu-item) {
  font-size: 14px !important;
  height: 38px !important;
  line-height: 38px !important;
  margin: 2px 0 !important;
  border-radius: 7px !important;
  color: #1d2129 !important;
}

:deep(.ant-menu-item:hover) {
  background: #f2f3f5 !important;
}

:deep(.ant-menu-item-selected) {
  background: #e8f3ff !important;
  color: #1664d8 !important;
  font-weight: 500;
}

:deep(.ant-menu-submenu-title) {
  font-size: 14px !important;
  height: 38px !important;
  line-height: 38px !important;
  margin: 2px 0 !important;
  border-radius: 7px !important;
  color: #1d2129 !important;
}

:deep(.ant-menu-submenu-title:hover) {
  background: #f2f3f5 !important;
}

:deep(.ant-menu-sub .ant-menu-item) {
  padding-left: 36px !important;
}
</style>
